import { isReadableAtom } from "../util/nanostores.js";
import { isGetHook, isMutatorHook, isStore } from "./client.js";
import { atom } from "nanostores";
import { computed as computed$1, getCurrentScope, isRef, onScopeDispose, ref, shallowRef, watch } from "vue";

//#region src/client/vue.ts
/**
* Converts a Vue Ref to a NanoStore Atom.
*
* This is used to convert Vue refs to atoms, so that we can use them in the store.
*
* @private
*/
function refToAtom(ref$1) {
	const a = atom(ref$1.value);
	watch(ref$1, (newVal) => {
		a.set(newVal);
	});
	return a;
}
function createVueHook(hook) {
	return ({ path, query } = {}) => {
		const pathParams = {};
		const queryParams = {};
		for (const [key, value] of Object.entries(path ?? {})) {
			const v = value;
			pathParams[key] = isRef(v) ? refToAtom(v) : v;
		}
		for (const [key, value] of Object.entries(query ?? {})) {
			const v = value;
			queryParams[key] = isRef(v) ? refToAtom(v) : v;
		}
		const store = hook.store({
			path: pathParams,
			query: queryParams
		});
		const data = ref();
		const loading = ref();
		const error = ref();
		const unsubscribe = store.subscribe((updatedStoreValue) => {
			data.value = updatedStoreValue.data;
			loading.value = updatedStoreValue.loading;
			error.value = updatedStoreValue.error;
		});
		if (getCurrentScope()) onScopeDispose(() => {
			unsubscribe();
		});
		return {
			data,
			loading,
			error
		};
	};
}
function createVueMutator(hook) {
	return () => {
		const store = useStore(hook.mutatorStore);
		const mutate = async (args) => {
			const { body, path, query } = args;
			const pathParams = {};
			const queryParams = {};
			for (const [key, value] of Object.entries(path ?? {})) {
				const v = value;
				pathParams[key] = isRef(v) ? v.value : v;
			}
			for (const [key, value] of Object.entries(query ?? {})) {
				const v = value;
				queryParams[key] = isRef(v) ? v.value : v;
			}
			return store.value.mutate({
				body,
				path: pathParams,
				query: queryParams
			});
		};
		return {
			mutate,
			loading: computed$1(() => store.value.loading),
			error: computed$1(() => store.value.error),
			data: computed$1(() => store.value.data)
		};
	};
}
function createVueStore(hook) {
	if (isReadableAtom(hook.obj)) return (() => useStore(hook.obj).value);
	return (() => {
		const result = {};
		for (const key in hook.obj) {
			if (!Object.prototype.hasOwnProperty.call(hook.obj, key)) continue;
			const value = hook.obj[key];
			if (isReadableAtom(value)) result[key] = useStore(value).value;
			else result[key] = value;
		}
		return result;
	});
}
function useFragno(clientObj) {
	const result = {};
	for (const key in clientObj) {
		if (!Object.prototype.hasOwnProperty.call(clientObj, key)) continue;
		const hook = clientObj[key];
		if (isGetHook(hook)) result[key] = createVueHook(hook);
		else if (isMutatorHook(hook)) result[key] = createVueMutator(hook);
		else if (isStore(hook)) result[key] = createVueStore(hook);
		else result[key] = hook;
	}
	return result;
}
function useStore(store) {
	const state = shallowRef();
	const unsubscribe = store.subscribe((value) => {
		state.value = value;
	});
	if (getCurrentScope()) onScopeDispose(unsubscribe);
	return state;
}

//#endregion
export { refToAtom, useFragno, useStore };
//# sourceMappingURL=vue.js.map